import { useMemo, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useGetEnabledFeatureFlagsQuery,
  useUserServiceGetTenantQuery,
} from '@/generated/graphql'
import { useGraphQLClient } from '@/libs/graphql-provider'
import { useTenantStore } from '@/stores/tenantStore'

// Export feature codes for reuse across the app
export const FEATURE_CODES = {
  DASHBOARD: 'dashboard',
  USER_MANAGEMENT: 'user-management',
  BRANCHES: 'branches',
  TENANTS: 'tenants',
  ORDERS: 'orders',
  RATINGS: 'ratings',
  BOOKINGS: 'bookings',
  FORMS: 'forms',
  SURVEYS: 'surveys',
  FNS_RESPONSES: 'fns-responses',
  ARTICLES: 'articles',
  EVENTS: 'events',
  CATEGORIES: 'categories',
  PRODUCTS: 'products',
  INVENTORIES: 'inventories',
  COLORS: 'colors',
  VOUCHER_TYPES: 'voucher-types',
  REFERRAL: 'referral',
  AFFILIATE_CONFIG: 'affiliate-config',
  SETTINGS: 'settings',
  GENERAL_SETTINGS: 'general-settings',
  CONTENT_SETTINGS: 'content-settings',
  APPEARANCE_SETTINGS: 'appearance-settings',
  SHIPPING_SETTINGS: 'shipping-settings',
  PAYMENT_TRANSACTIONS: 'payment-transactions',
  POINT_MANAGEMENT: 'point-management',
  LOYALTY_PROGRAMS: 'loyalty-programs',
  MINI_GAMES: 'mini-games',
  FLASH_SALES: 'flash-sales',
  MESSAGE_MANAGEMENT: 'message-management',
  ZALO: 'zalo',
  ZALO_ZNS: 'zalo-zns',
  ZALO_OA: 'zalo-oa',
  ZALO_SCENARIOS: 'zalo-scenarios',
  ZALO_REPORTS: 'zalo-reports',
  FEATURE_MANAGEMENT: 'feature-management',
  API_HUB: 'api-hub',
  SHARE_LINKS: 'share-links',
  ORDER_SHIPPING: 'order-shipping',
  GAME_TEMPLATES: 'game-templates',
} as const

export type FeatureCode = (typeof FEATURE_CODES)[keyof typeof FEATURE_CODES]

/**
 * Hook to get enabled feature flags for the current tenant
 */
export function useEnabledFeatureFlags() {
  const client = useGraphQLClient()
  const queryClient = useQueryClient()
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId)
  const selectedTenant = useTenantStore((state) => state.selectedTenant)

  // Fetch tenant information to get the plan type
  const {
    data: tenantData,
    isLoading: tenantLoading,
    error: tenantError,
  } = useUserServiceGetTenantQuery(
    client,
    {},
    {
      enabled: !!selectedTenantId && !selectedTenant,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      queryKey: selectedTenantId
        ? ['userServiceGetTenant', { tenantId: selectedTenantId }]
        : undefined,
    },
    selectedTenantId ? { 'x-tenant-id': selectedTenantId } : undefined
  )

  const plan =
    selectedTenant?.type || tenantData?.userServiceTenantById?.type || ''

  // Invalidate feature flag queries when tenant changes
  useEffect(() => {
    if (selectedTenantId) {
      // Invalidate all feature flag related queries for clean slate
      queryClient.invalidateQueries({
        queryKey: ['getEnabledFeatureFlags'],
      })
    }
  }, [selectedTenantId, queryClient])

  const { data, isLoading, error, refetch } = useGetEnabledFeatureFlagsQuery(
    client,
    {
      input: {
        tenantId: selectedTenantId || undefined,
        plan: plan,
      },
    },
    {
      enabled: !!selectedTenantId && !!plan && !tenantLoading,
      staleTime: 2 * 60 * 1000, // 2 minutes (shorter than sidebar cache)
      refetchOnWindowFocus: false,
      // Add tenant ID and plan to query key for proper cache separation
      queryKey:
        selectedTenantId && plan
          ? ['getEnabledFeatureFlags', { tenantId: selectedTenantId, plan }]
          : undefined,
    },
    selectedTenantId ? { 'x-tenant-id': selectedTenantId } : undefined
  )

  const enabledFeatureCodes = useMemo(() => {
    if (!data?.featureFlagServiceGetEnabledFeatureFlags)
      return new Set<string>()

    return new Set(
      data.featureFlagServiceGetEnabledFeatureFlags
        .map((flag) => flag.featureCode)
        .filter((code): code is string => !!code)
    )
  }, [data])

  return {
    enabledFeatureCodes,
    isLoading: tenantLoading || isLoading,
    error: tenantError || error,
    refetch,
    plan, // Expose plan for debugging/info purposes
  }
}

/**
 * Hook to check if a specific feature is enabled
 */
export function useFeatureFlag(featureCode: FeatureCode | string) {
  const { enabledFeatureCodes, isLoading } = useEnabledFeatureFlags()

  return {
    isEnabled: enabledFeatureCodes.has(featureCode),
    isLoading,
  }
}

/**
 * Hook to check multiple features at once
 */
export function useFeatureFlags(featureCodes: (FeatureCode | string)[]) {
  const { enabledFeatureCodes, isLoading } = useEnabledFeatureFlags()

  const enabledFlags = useMemo(() => {
    const result: Record<string, boolean> = {}
    featureCodes.forEach((code) => {
      result[code] = enabledFeatureCodes.has(code)
    })
    return result
  }, [featureCodes, enabledFeatureCodes])

  return {
    enabledFlags,
    isLoading,
    isEnabled: (featureCode: string) => enabledFlags[featureCode] || false,
  }
}
